import { Select as AntSelect } from "antd";
import { Option, ReadonlyArray, Runtime } from "fp";
import React from "react";

import { ID } from "~/ID";
import * as Loading from "~/Loading";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { Search } from "~/Search";

export type Type = {
  readonly type: ID & {
    readonly name?: string;
    readonly display?: string;
  };
};

export function Type(type: Type) {
  return pipe(
    Type.use(type),
    Remote.map(Option.map((type) => <>{Type.toString(type)}</>)),
    Remote.getOrElseW(() => Option.none),
    Option.getOrElse(() => <Loading.Component />)
  );
}

export namespace Type {
  export const hasBreak = ({ type: { id } }: Type): boolean =>
    ["2", "3", "13", "14", "15", "16"].includes(id);

  export const toString = ({ type: { id, name, display } }: Type) =>
    display ?? `Exemption ${name ?? id}`;

  export const toSearch: Search.From<Type> = ({
    type: { id, name, display },
  }) =>
    Search.join(
      Search.string(`Exemption ${id}`),
      name ? Search.string(name) : Search.empty(),
      display ? Search.string(display) : Search.empty()
    );

  export const use = ({ type: { id } }: Type): Remote<Option<Type>> =>
    pipe(
      Types.use(),
      Remote.map(
        flow(
          ({ types }) => types,
          ReadonlyArray.findFirst(({ type }) => id === type.id)
        )
      )
    );

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        hours_of_service_type_id: Runtime.number(),
        name: Runtime.nullable(Runtime.string()),
        display_name: Runtime.nullable(Runtime.string()),
      });

    export const decode = (response: Runtime.ToType<typeof runtime>): Type => ({
      type: {
        ...ID.fromNumber(response.hours_of_service_type_id),
        name: response.name ?? undefined,
        display: response.display_name ?? undefined,
      },
    });
  }

  export function Select({ selected, select }: Select.Props) {
    const types = Types.use();
    const loading = Remote.isLoading(types);
    const options = pipe(
      types,
      Remote.getOrElse(Types.empty),
      Types.Select.Options.fromType
    );

    return (
      <AntSelect
        loading={loading}
        value={selected?.type.id}
        onChange={(id) => select({ type: { id } })}
        options={
          (options as never) as React.ComponentProps<
            typeof AntSelect
          >["options"]
        }
      />
    );
  }

  export namespace Select {
    export type Props = {
      readonly selected?: Type;
      readonly select: (type?: Type) => void;
    };

    export const use = (initial?: Partial<Type>) => {
      const [selected, setSelected] = React.useState(
        initial?.type && { type: initial.type }
      );

      return { selected, select: setSelected };
    };

    export const initialize = ({ select }: ReturnType<typeof use>) => (
      initial?: Partial<Type>
    ) => {
      if (initial?.type) {
        select({ type: initial.type });
      }
    };
  }
}

export type Types = { readonly types: readonly Type[] };
export namespace Types {
  export const empty = (): Types => ({ types: [] });

  export const use = (): Remote<Types> => {
    return Remote.Query.use("Hours of Service Types", request());
  };

  export const request = (): Request.Fn<Types> =>
    flow(
      Request.fromConfig({ endpoint: "/hours-of-service-types" }),
      Request.Expect.status(200),
      Request.Expect.json(Runtime.array(Type.Response.runtime())),
      Request.onSuccess(
        flow(ReadonlyArray.map(Type.Response.decode), (types) => ({ types }))
      )
    );

  export function Select({ selected, select, clear, ...props }: Select.Props) {
    const types = use();
    const loading = Remote.isLoading(types);
    const value = (selected?.types ?? []).map(({ type: { id } }) => id);
    const options = pipe(
      types,
      Remote.getOrElse(empty),
      Select.Options.fromType
    );

    const onChange = (ids: readonly string[]) =>
      pipe(
        options,
        ReadonlyArray.filterMap(({ type }) =>
          ids.includes(type.id) ? Option.some({ type }) : Option.none
        ),
        (types) => select({ types })
      );

    const filterOption = (value: string, option: Select.Option) =>
      Search.matches(Search.Match.string(value))(Type.toSearch(option));

    type AntSelectProps = React.ComponentProps<typeof AntSelect>;
    return (
      <AntSelect
        {...props}
        mode="multiple"
        loading={loading}
        value={value}
        onChange={onChange}
        onClear={clear}
        filterOption={filterOption as AntSelectProps["filterOption"]}
        options={(options as never) as AntSelectProps["options"]}
      />
    );
  }

  export namespace Select {
    export type Props = Controls &
      Pick<
        React.ComponentProps<typeof AntSelect>,
        "bordered" | "placeholder" | "style"
      >;

    export type Controls = {
      readonly selected?: Partial<Types>;
      readonly select: (types: Partial<Types>) => void;
      readonly clear: () => void;
    };

    export namespace Controls {
      export const use = (): Controls => {
        const [selected, setSelected] = React.useState<Types>(empty());
        return {
          selected,
          clear: () => setSelected(empty()),
          select: ({ types = [] }) =>
            pipe(
              [...selected.types, ...types],
              ReadonlyArray.uniq({ equals: (a, b) => a.type.id === b.type.id }),
              (types) => setSelected({ types })
            ),
        };
      };
    }

    export type Option = Type & {
      readonly value: string;
      readonly label: string;
    };

    export type Options = readonly Option[];
    export namespace Options {
      export const fromType = ({ types }: Types): Options =>
        pipe(
          types,
          ReadonlyArray.sort<Type>(
            Order.fromCompare((a, b) =>
              Order.string().compare(Type.toString(a), Type.toString(b))
            )
          ),
          ReadonlyArray.map(({ type }) => ({
            type,
            value: type.id,
            label: Type.toString({ type }),
          }))
        );
    }
  }
}
